import type { Answer, GamePhase, Question } from "./types";
import { useMutation } from "@tanstack/react-query";
import { AnimatePresence } from "framer-motion";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import questionsData from "@/data/apex-style-questions.json";
import { analyzeApexStyle } from "@/lib/client/apex-style";
import { LoadingOverlay } from "./loading-overlay";
import { positions } from "./positions";
import { PositionSelect } from "./position-select";
import { QuizSection } from "./quiz-section";
import { ResultSection } from "./result-section";

// 随机选择题目的函数
function getRandomQuestions(questions: Question[], count: number): Question[] {
  const shuffled = [...questions].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, count);
}

export function ApexStyleQuiz() {
  const [phase, setPhase] = useState<GamePhase>("position-select");
  const [selectedPosition, setSelectedPosition] = useState<number | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [streamingText, setStreamingText] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [questionKey, setQuestionKey] = useState(0); // 用于触发题目重新随机
  const [showAnswersDialog, setShowAnswersDialog] = useState(false);

  // 随机选择10个题目
  const randomQuestions = useMemo(() => {
    return getRandomQuestions(questionsData.questions, 10);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionKey]); // 依赖questionKey来重新生成题目

  // 获取选中位置的信息
  const selectedPositionInfo = positions.find((p) => p.id === selectedPosition);

  const { mutate: analyzeStyle, isPending } = useMutation({
    mutationFn: async (data: { selectedPosition: number; answers: Answer[] }) => {
      setIsStreaming(true);
      setStreamingText("");
      setPhase("result");

      const response = await analyzeApexStyle(data);

      await processTextStream({
        stream: response.body!,
        onTextPart: (text) => {
          setStreamingText((_) => _ + text);
        },
      });
    },
    onSuccess: () => {
      setIsStreaming(false);
    },
    onError: (error) => {
      setIsStreaming(false);
      toast.error(error.message || "分析失败，请重试");
      setPhase("quiz");
    },
  });

  const currentQuestion = randomQuestions[currentQuestionIndex];

  const handlePositionSelect = (positionId: number) => {
    setSelectedPosition(positionId);
  };

  const handleStartQuiz = () => {
    if (!selectedPosition) {
      toast.error("请先选择一个位置");
      return;
    }
    setPhase("quiz");
  };

  const handleAnswerSelect = (optionId: string, optionText: string) => {
    if (!currentQuestion) return;

    const newAnswer: Answer = {
      index: currentQuestionIndex,
      selectedOption: optionId,
      questionText: currentQuestion.question,
      answerText: optionText,
    };

    const newAnswers = [...answers, newAnswer];
    setAnswers(newAnswers);

    if (currentQuestionIndex < randomQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      // 完成所有问题，开始分析
      analyzeStyle({
        selectedPosition: selectedPosition!,
        answers: newAnswers,
      });
    }
  };

  const handlePrevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      setAnswers(answers.slice(0, -1));
    }
  };

  const handleRestart = () => {
    setPhase("position-select");
    setSelectedPosition(null);
    setCurrentQuestionIndex(0);
    setAnswers([]);
    setStreamingText("");
    setIsStreaming(false);
    setQuestionKey((prev) => prev + 1); // 触发题目重新随机
    setShowAnswersDialog(false); // 关闭答题记录对话框
  };

  return (
    <div className="max-w-4xl p-4">
      <AnimatePresence mode="wait">
        {phase === "position-select" && (
          <PositionSelect
            selectedPosition={selectedPosition}
            onPositionSelect={handlePositionSelect}
            onStartQuiz={handleStartQuiz}
          />
        )}

        {phase === "quiz" && currentQuestion && (
          <QuizSection
            currentQuestion={currentQuestion}
            currentQuestionIndex={currentQuestionIndex}
            totalQuestions={randomQuestions.length}
            onAnswerSelect={handleAnswerSelect}
            onPrevQuestion={handlePrevQuestion}
            canGoPrev={currentQuestionIndex > 0}
          />
        )}

        {phase === "result" && (
          <ResultSection
            streamingText={streamingText}
            isStreaming={isStreaming}
            answers={answers}
            selectedPositionInfo={selectedPositionInfo}
            showAnswersDialog={showAnswersDialog}
            onShowAnswersDialogChange={setShowAnswersDialog}
            onRestart={handleRestart}
          />
        )}
      </AnimatePresence>

      <LoadingOverlay show={isPending && !isStreaming} />
    </div>
  );
}

async function processTextStream({
  stream,
  onTextPart,
}: {
  stream: ReadableStream<Uint8Array>;
  onTextPart: (chunk: string) => Promise<void> | void;
}): Promise<void> {
  const decoder = new TextDecoder();
  const reader = stream.getReader();
  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    const textPart = decoder.decode(value, { stream: true });
    if (textPart) {
      await onTextPart(textPart);
    }
  }

  const tail = decoder.decode();
  if (tail) {
    await onTextPart(tail);
  }
}
